// ST-1705 -Claude- Business portal shell. Existing /admin/* paths are kept as
// implementation routes, but the user-facing product is the business portal.
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { isNearrmeAdminRole, listCompanies, logout } from './api';
import { hasAnyPermission, ROUTE_POLICIES } from './permissions';
import { useAdminAuth } from './useAdminAuth';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    items: [
  { to: '/admin', label: 'Dashboard', icon: 'dashboard' },
  { to: '/admin/companies', label: 'Companies', icon: 'building' },
  { to: '/admin/brands', label: 'Brands', icon: 'badge' },
  { to: '/admin/stores', label: 'Stores', icon: 'store' },
  { to: '/admin/products', label: 'Products', icon: 'box' },
  { to: '/admin/orders', label: 'Orders', icon: 'receipt' },
  { to: '/admin/consumers', label: 'Consumers', icon: 'users' },
  { to: '/admin/nearrme-team', label: 'Nearrme Team', icon: 'shield' },
  { to: '/admin/company-team', label: 'Company Team', icon: 'team' },
  { to: '/admin/contact-messages', label: 'Messages', icon: 'message' },
  { to: '/admin/help', label: 'Help', icon: 'help' },
  { to: '/admin/agents', label: 'Assistance', icon: 'headset' },
    ],
  },
  {
    label: 'Setup',
    items: [
      { to: '/admin/setup/login-options', label: 'Login Options', icon: 'key' },
      { to: '/admin/setup/roles', label: 'Roles', icon: 'roles' },
      { to: '/admin/setup/permissions', label: 'Permissions', icon: 'lock' },
      { to: '/admin/setup/activity', label: 'Activity', icon: 'activity' },
    ],
  },
];

type IconName =
  | 'activity'
  | 'badge'
  | 'box'
  | 'building'
  | 'chevron'
  | 'collapse'
  | 'dashboard'
  | 'headset'
  | 'help'
  | 'key'
  | 'lock'
  | 'message'
  | 'receipt'
  | 'roles'
  | 'shield'
  | 'store'
  | 'team'
  | 'users';

const ICON_PATHS: Record<IconName, string[]> = {
  activity: ['M3 12h4l2-7 4 14 2-7h6'],
  badge: ['M12 3l2.2 4.5 5 .7-3.6 3.5.9 5-4.5-2.4-4.5 2.4.9-5L4.8 8.2l5-.7L12 3z'],
  box: ['M4 7.5 12 3l8 4.5v9L12 21l-8-4.5v-9z', 'M4 7.5l8 4.5 8-4.5', 'M12 12v9'],
  building: ['M4 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16', 'M16 8h2a2 2 0 0 1 2 2v11', 'M8 7h4M8 11h4M8 15h4M9 21v-3h2v3'],
  chevron: ['M9 6l6 6-6 6'],
  collapse: ['M4 5h16M4 12h16M4 19h16'],
  dashboard: ['M4 13a8 8 0 1 1 16 0', 'M12 13l4-4', 'M5 19h14'],
  headset: ['M4 13v-1a8 8 0 0 1 16 0v1', 'M4 13h3v5H4v-5zM17 13h3v5h-3v-5z', 'M17 18c0 2-2 3-5 3'],
  help: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M9.8 9a2.4 2.4 0 1 1 3.9 1.9c-.9.6-1.7 1.2-1.7 2.6', 'M12 17h.01'],
  key: ['M15 7a4 4 0 1 0-3 6.8L5 21H3v-2l7.2-7.2A4 4 0 0 0 15 7z', 'M14 7h.01'],
  lock: ['M6 10V8a6 6 0 0 1 12 0v2', 'M5 10h14v11H5V10z'],
  message: ['M4 5h16v11H7l-3 3V5z'],
  receipt: ['M6 3h12v18l-2-1-2 1-2-1-2 1-2-1-2 1V3z', 'M9 8h6M9 12h6M9 16h4'],
  roles: ['M8 7a4 4 0 1 0 0 8 4 4 0 0 0 0-8z', 'M16 8l4 2v4c0 3-2 5-4 6-2-1-4-3-4-6v-4l4-2z'],
  shield: ['M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6l7-3z'],
  store: ['M4 10h16l-1-5H5l-1 5z', 'M6 10v10h12V10', 'M9 20v-6h6v6', 'M7 14h.01M17 14h.01'],
  team: ['M8 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M2 21a6 6 0 0 1 12 0', 'M17 10a3 3 0 1 0 0-6', 'M15 21a5 5 0 0 1 7-4.6'],
  users: ['M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z', 'M3 21a6 6 0 0 1 12 0', 'M17 11a3 3 0 1 0 0-6', 'M16 21a5 5 0 0 1 5-5'],
};

function MenuIcon({ name, className = '' }: { name: IconName; className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={`h-5 w-5 shrink-0 ${className}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {ICON_PATHS[name].map((path) => (
        <path key={path} d={path} />
      ))}
    </svg>
  );
}

export function AdminLayout() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isNearrmeAdmin = isNearrmeAdminRole(user?.role);
  const isNearrmeUser = user?.role === 'NearrmeUser';
  const isCompanyShell = !!user && !isNearrmeAdmin && !isNearrmeUser;
  const isSetupRoute = location.pathname.startsWith('/admin/setup') ||
    ['/admin/login-options', '/admin/roles', '/admin/permissions', '/admin/logs'].includes(location.pathname);
  const [setupOpen, setSetupOpen] = useState(isSetupRoute);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { data: shellCompanies = [] } = useQuery({
    queryKey: ['admin-shell-company', user?.companyCode],
    queryFn: listCompanies,
    enabled: isCompanyShell,
    staleTime: 5 * 60 * 1000,
  });
  const assignedCompany = shellCompanies.find((company) => company.companyCode === user?.companyCode);
  const roleLabel = isNearrmeAdmin ? 'Nearrme Admin' : isNearrmeUser ? 'Nearrme User' : 'Company User';
  const scopeLabel = isNearrmeAdmin || isNearrmeUser ? 'Network-wide access' : `Company scope: ${user?.companyCode ?? 'Assigned company'}`;
  const consoleName = isCompanyShell
    ? assignedCompany?.companyName ?? user?.companyCode ?? 'Company Portal'
    : 'Nearrme Console';
  const companyInitials = (assignedCompany?.companyName ?? user?.companyCode ?? 'Company')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'C';

  useEffect(() => {
    if (isSetupRoute) {
      setSetupOpen(true);
    }
  }, [isSetupRoute]);

  const handleLogout = async () => {
    await logout();
    navigate('/business/login', { replace: true });
  };

  return (
    <div className={`min-h-screen bg-slate-100 dark:bg-slate-950 md:grid ${sidebarCollapsed ? 'md:grid-cols-[5rem_minmax(0,1fr)]' : 'md:grid-cols-[18rem_minmax(0,1fr)]'}`}>
      <aside className="w-full bg-white dark:bg-slate-900 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-800 flex flex-col md:sticky md:top-0 md:h-screen md:max-h-screen">
        <div className={`h-20 flex items-center border-r md:border-r-0 md:border-b border-slate-200 dark:border-slate-800 shrink-0 ${sidebarCollapsed ? 'justify-center px-3' : 'justify-between px-4 md:px-6'}`}>
          <Link to="/admin" className="flex items-center gap-2 min-w-0">
            {isCompanyShell && assignedCompany?.logoUrl ? (
              <span className="grid h-10 w-10 place-items-center overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-700">
                <img
                  src={assignedCompany.logoUrl}
                  alt={`${assignedCompany.companyName ?? 'Company'} logo`}
                  className="h-full w-full object-contain p-1"
                />
              </span>
            ) : (
              <span className="grid h-9 w-9 place-items-center rounded-lg bg-brand-600 text-sm font-bold text-white">
                {isCompanyShell ? companyInitials : 'N'}
              </span>
            )}
            <span className={`min-w-0 ${sidebarCollapsed ? 'md:hidden' : ''}`}>
              <span className="block font-semibold text-base text-slate-950 dark:text-slate-50 truncate">{consoleName}</span>
              <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">{roleLabel}</span>
            </span>
          </Link>
          <button
            type="button"
            onClick={() => setSidebarCollapsed((current) => !current)}
            className={`hidden md:grid h-9 w-9 place-items-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 dark:border-slate-800 dark:text-slate-300 dark:hover:bg-slate-800 ${sidebarCollapsed ? 'absolute left-14 top-5 bg-white dark:bg-slate-900' : ''}`}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <MenuIcon name={sidebarCollapsed ? 'chevron' : 'collapse'} className={sidebarCollapsed ? '' : 'h-4 w-4'} />
          </button>
        </div>

        <nav className={`flex-1 min-h-0 px-2 py-2 md:py-4 flex md:block gap-1 overflow-x-auto md:overflow-x-hidden md:overflow-y-auto ${sidebarCollapsed ? 'md:px-3 md:space-y-2' : 'md:px-4 md:space-y-2'}`}>
          {NAV_GROUPS.map((group, groupIndex) => {
            const items = group.items.filter((item) => {
              return hasAnyPermission(user, ROUTE_POLICIES[item.to] ?? []);
            });
            if (items.length === 0) return null;

            return (
              <div key={group.label ?? groupIndex} className="contents md:block">
                {group.label === 'Setup' && (
                  <button
                    type="button"
                    onClick={() => setSetupOpen((current) => !current)}
                    className={`flex w-full items-center rounded-md px-3 py-2.5 text-left text-sm font-semibold whitespace-nowrap transition-colors ${sidebarCollapsed ? 'md:justify-center' : 'gap-3'} ${
                      isSetupRoute
                        ? 'bg-slate-100 text-slate-950 dark:bg-slate-800 dark:text-slate-50'
                        : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                    }`}
                    aria-expanded={setupOpen}
                    aria-controls="setup-nav-items"
                    title="Setup"
                  >
                    <MenuIcon name="key" />
                    <span className={`flex-1 ${sidebarCollapsed ? 'md:hidden' : ''}`}>Setup</span>
                    <MenuIcon name="chevron" className={`h-4 w-4 transition-transform ${setupOpen ? 'rotate-90' : ''} ${sidebarCollapsed ? 'md:hidden' : ''}`} />
                  </button>
                )}
                <div className="flex gap-1 md:block md:space-y-1">
                  {items.map((item) => {
                    if (group.label === 'Setup' && (!setupOpen || sidebarCollapsed)) return null;
                    return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.to === '/admin'}
                      className={({ isActive }) =>
                        `flex items-center ${sidebarCollapsed ? 'md:justify-center' : 'gap-3'} ${group.label === 'Setup' && !sidebarCollapsed ? 'md:ml-5' : ''} px-3 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                          isActive
                            ? 'bg-slate-950 dark:bg-slate-100 text-white dark:text-slate-950'
                            : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                        }`
                      }
                      title={item.label}
                    >
                      <MenuIcon name={item.icon} />
                      <span className={sidebarCollapsed ? 'md:hidden' : ''}>{item.label}</span>
                    </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        <div className={`hidden md:block shrink-0 border-t border-slate-200 dark:border-slate-800 ${sidebarCollapsed ? 'p-3' : 'p-4'}`}>
          {user && (
            <div className={`mb-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-sm ${sidebarCollapsed ? 'grid place-items-center p-2' : 'p-3'}`}>
              {sidebarCollapsed ? (
                <span className="grid h-9 w-9 place-items-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200" title={user.email ?? ''}>
                  {(user.email ?? 'U').slice(0, 1).toUpperCase()}
                </span>
              ) : (
              <>
              <div className="font-medium text-slate-900 dark:text-slate-100 truncate" title={user.email ?? ''}>
                {user.email}
              </div>
              <div className="mt-1 text-xs font-medium text-slate-600 dark:text-slate-300">
                {roleLabel}
              </div>
              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {scopeLabel}
              </div>
              </>
              )}
            </div>
          )}
          <button
            onClick={handleLogout}
            className="w-full px-3 py-2 text-sm font-medium rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            title="Log out"
          >
            {sidebarCollapsed ? 'Exit' : 'Log out'}
          </button>
          {isCompanyShell && !sidebarCollapsed && (
            <div className="mt-3 text-center text-[11px] font-medium text-slate-500 dark:text-slate-400">
              Powered by Nearrme
            </div>
          )}
        </div>
      </aside>

      <main className="min-w-0">
        <div className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-4 sm:px-6">
          <div className="mx-auto flex max-w-7xl flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{scopeLabel}</div>
              <div className="text-sm text-slate-600 dark:text-slate-300">
                {isNearrmeAdmin
                  ? 'Manage the Nearrme network across companies, business locations, products, consumers, orders, and operations.'
                  : 'Manage only the company, business locations, products, consumers, orders, and messages assigned to your company.'}
              </div>
            </div>
            <Link to="/" className="text-sm font-medium text-brand-700 dark:text-brand-300 hover:underline">
              View public site
            </Link>
          </div>
        </div>
        <div className="max-w-7xl mx-auto p-4 sm:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
